export const SYSTEM_ORDER = ["VITA_CLASSICAL", "VITA_3D_MASTER", "CHROMASCOP", "CLAREAMENTO", "OUTRO"] as const;

export const SYSTEM_LABELS: Record<string, string> = {
  VITA_CLASSICAL: "VITA Classical",
  VITA_3D_MASTER: "VITA 3D-Master",
  CHROMASCOP: "Chromascop",
  // Código histórico da base; BL1–BL4 são as cores de clareamento da Ivoclar.
  CLAREAMENTO: "Ivoclar BL (Bleach)",
  OUTRO: "Outro",
};

/** Sistema criado pelo laboratório é gravado com o nome digitado — o rótulo é o próprio nome. */
export function systemLabel(system: string): string {
  return SYSTEM_LABELS[system] ?? system;
}

/** Escalas de fábrica primeiro, na ordem conhecida; sistemas criados pelo laboratório depois, em
 *  ordem alfabética. */
export function orderedSystems(items: Shade[]): string[] {
  const presentes = new Set(items.map((i) => i.system));
  const padrao = SYSTEM_ORDER.filter((s) => presentes.has(s));
  const customizados = [...presentes]
    .filter((s) => !(SYSTEM_ORDER as readonly string[]).includes(s))
    .sort((a, b) => a.localeCompare(b, "pt-BR"));
  return [...padrao, ...customizados];
}

export type Shade = {
  id: string;
  system: string;
  code: string;
  active?: boolean;
  originGlobalId?: string | null;
  origemNaBaseId?: string | null;
  /** Em que materiais esta cor existe. Vazio = vale pra todos (ver MaterialCategoryShadeOption). */
  compatibleMaterialCategoryIds?: string[];
};
export type Subgroup = [string, Shade[]];

/** Prefixo do código (letra ou dígito inicial) — agrupa A1-A4/B1-B4/... na Classical e 1M1/2L1.5/...
 *  pelo grupo de valor (1-5) na 3D-Master. Convenção padrão de guia de cor odontológico. */
export function subgroupKey(code: string): string {
  return code.match(/^[A-Za-z]+|^\d+/)?.[0] ?? code;
}

export function groupBySystem<T extends Shade>(items: T[]) {
  return orderedSystems(items).map((system) => {
    const systemItems = items.filter((i) => i.system === system);
    const subgroupMap = new Map<string, T[]>();
    for (const item of systemItems) {
      const key = subgroupKey(item.code);
      if (!subgroupMap.has(key)) subgroupMap.set(key, []);
      subgroupMap.get(key)!.push(item);
    }
    const subgroups: Subgroup[] = [...subgroupMap.entries()].sort(([a], [b]) =>
      a.localeCompare(b, undefined, { numeric: true }),
    );
    return { system, items: systemItems, subgroups };
  });
}

/** Colunas = número real de sistemas ativos, não um número fixo — senão sobra espaço vazio
 *  reservado pra colunas que não existem (ex: só 2 sistemas globais hoje, não 4). */
export function groupsGridCls(count: number): string {
  if (count <= 1) return "grid-cols-1";
  if (count === 2) return "grid-cols-1 sm:grid-cols-2";
  if (count === 3) return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";
  return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4";
}

export function splitInHalf<T>(arr: T[]): [T[], T[]] {
  const half = Math.ceil(arr.length / 2);
  return [arr.slice(0, half), arr.slice(half)];
}

/** Conta as cores ativas por escala — usado no resumo do hub do Catálogo. */
export function countByShadeSystem(shades: Shade[]) {
  const active = shades.filter((s) => s.active !== false);
  return orderedSystems(active).map((system) => ({
    system,
    label: systemLabel(system),
    count: active.filter((s) => s.system === system).length,
  }));
}
